import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const LicenseCreate = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    license_key: '',
    tier_id: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    start_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    max_users: 1,
    status: 'active',
    notes: '',
  });
  const [tiers, setTiers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      const response = await api.getLicenseTiers();
      if (response.success) {
        setTiers(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load tiers:', error);
    }
  };

  const handleTierChange = (e) => {
    const tierId = e.target.value;
    const selectedTier = tiers.find(t => t.id === parseInt(tierId));
    setFormData({
      ...formData,
      tier_id: tierId,
      max_users: selectedTier ? selectedTier.max_users : formData.max_users,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleGenerateKey = async () => {
    try {
      const response = await api.generateLicenseKey();
      if (response.success) {
        setFormData({ ...formData, license_key: response.data.license_key });
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.createLicense(formData);
      if (response.success) {
        navigate('/licenses');
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Create New License</h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="form-group">
                  <label>License Tier</label>
                  <select
                    className="form-control"
                    name="tier_id"
                    value={formData.tier_id}
                    onChange={handleTierChange}
                  >
                    <option value="">Select a tier (optional)</option>
                    {tiers.map(tier => (
                      <option key={tier.id} value={tier.id}>
                        {tier.name} - {tier.currency} {tier.price_monthly}/month
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>License Key</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      name="license_key"
                      value={formData.license_key}
                      onChange={handleChange}
                      placeholder="Leave empty to auto-generate"
                    />
                    <div className="input-group-append">
                      <button type="button" className="btn btn-default" onClick={handleGenerateKey}>
                        <i className="fas fa-key"></i> Generate
                      </button>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Contact Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Contact Email</label>
                      <input
                        type="email"
                        className="form-control"
                        name="contact_email"
                        value={formData.contact_email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="text"
                    className="form-control"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Start Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Expiry Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="expiry_date"
                        value={formData.expiry_date}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Max Users *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="max_users"
                        value={formData.max_users}
                        onChange={handleChange}
                        min="1"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                      >
                        <option value="active">Active</option>
                        <option value="expiring">Expiring</option>
                        <option value="expired">Expired</option>
                        <option value="suspended">Suspended</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    rows="3"
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="card-footer">
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                  <i className="fas fa-save"></i> {loading ? 'Creating...' : 'Create License'}
                </button>
                <button type="button" className="btn btn-default btn-sm ml-2" onClick={() => navigate('/licenses')}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default LicenseCreate;


